/*
 * FormatFrames — the three output shapes, drawn at their true ratios.
 *
 * The ratios and names are the ones the product already offers
 * (SettingsPanel / NewProject "Aspect ratio"; backend/models.py
 * aspect_ratio: "16:9" | "9:16" | "1:1"). Only the vertical option
 * names platforms in the app, so only it names them here. Each slash
 * is bound to the word before it (no-break space), so a narrow column
 * never starts a line with "/".
 *
 * Encoding: every frame shares one height and takes its width from CSS
 * aspect-ratio, so the shapes compare honestly — no frame is scaled to
 * "look nicer". From `md` up the row fills the band: the grid columns
 * are in the frames' width ratio (16/9 : 9/16 : 1 = 256 : 81 : 144), so
 * each frame is `w-full` of its column, its height follows from its
 * aspect-ratio, and all three land on the same height at any width.
 * Below `md` the frames are a fixed h-20 in rows, labels beside them. Inside each, the same landscape screen is fitted to the
 * frame's width and centred, which is what the renderer does
 * (scale=…:force_original_aspect_ratio=decrease + pad): a screenshot is
 * letterboxed into a vertical or square canvas, not re-laid out.
 *
 * The frames are decorative (aria-hidden); the ratio, name and platforms
 * beside each one are real text. Static — no motion to reduce.
 */

const FORMATS = [
  { ratio: "16:9", name: "Landscape", aspect: "aspect-video" },
  { ratio: "9:16", name: "Vertical",  aspect: "aspect-[9/16]", where: "Reels\u00a0/ Shorts\u00a0/ TikTok" },
  { ratio: "1:1",  name: "Square",    aspect: "aspect-square" },
];

/* The screen being demoed: a 16:9 product UI in the HeroStudio idiom,
   built only from the stage-neutral ramp plus one red primary button. */
function Screen() {
  return (
    <svg viewBox="0 0 160 90" className="block w-full h-auto" focusable="false">
      <rect x="0" y="0" width="160" height="90" fill="var(--ink-900)" />
      {/* sidebar */}
      <rect x="0" y="0" width="30" height="90" fill="var(--ink-850)" stroke="var(--ink-700)" />
      {[12, 20, 28, 36].map((y, i) => (
        <rect key={y} x="6" y={y} width={i === 1 ? 14 : 18} height="3"
              fill={i === 1 ? "var(--accent)" : "var(--ink-600)"} />
      ))}
      {/* toolbar */}
      <rect x="30" y="0" width="130" height="11" fill="var(--ink-850)" stroke="var(--ink-700)" />
      <rect x="37" y="4" width="26" height="3" fill="var(--ink-550)" />
      {/* content cards */}
      <rect x="38" y="19" width="54" height="26" fill="var(--ink-800)" stroke="var(--ink-700)" />
      <rect x="43" y="24" width="22" height="3" fill="var(--ink-550)" />
      <rect x="43" y="31" width="36" height="8" fill="var(--ink-750)" />
      <rect x="98" y="19" width="54" height="26" fill="var(--ink-800)" stroke="var(--ink-700)" />
      {[6, 11, 8, 15, 12].map((h, i) => (
        <rect key={i} x={104 + i * 9} y={40 - h} width="6" height={h}
              fill={i === 3 ? "var(--accent)" : "var(--ink-650)"} />
      ))}
      {/* the primary button, and the field beside it */}
      <rect x="38" y="53" width="40" height="11" fill="var(--accent)" />
      <rect x="44" y="57" width="28" height="3" fill="var(--text-on-accent)" />
      <rect x="84" y="53" width="68" height="11" fill="var(--ink-800)" stroke="var(--ink-700)" />
      {/* cursor dot: white, not red — red on the red button is a smudge */}
      <circle cx="61" cy="58.5" r="7" fill="none" stroke="var(--text-inverse)"
              strokeWidth="1.5" vectorEffect="non-scaling-stroke" />
      <circle cx="61" cy="58.5" r="2.2" fill="var(--text-inverse)" />
    </svg>
  );
}

function Frame({ aspect }) {
  return (
    <div
      aria-hidden="true"
      className={`relative ${aspect} h-20 md:h-auto md:w-full flex items-center bg-surface-inverse border border-rule shadow-brutal overflow-hidden`}
    >
      <Screen />
      {/* burnt-in caption bar, seated near the bottom of the canvas */}
      <div className="absolute inset-x-[14%] bottom-[7%] h-[9%] flex flex-col justify-center items-center gap-[12%] bg-surface-inverse/85 border border-ink-inverse/25">
        <span className="block w-4/5 h-[18%] bg-ink-inverse/90" />
        <span className="block w-1/2 h-[18%] bg-ink-inverse/90" />
      </div>
    </div>
  );
}

export default function FormatFrames() {
  return (
    <figure className="m-0 border border-rule bg-canvas">
      <figcaption className="px-5 py-5 md:px-8 border-b border-rule">
        <span className="eyebrow">Every format</span>
        <p className="font-display font-black type-subheading tracking-tight mt-2">
          Landscape, vertical or square.
        </p>
        <p className="font-mono text-sm text-ink-muted mt-2 max-w-prose">
          Choose the aspect ratio when you create a project, or change it in
          the project’s settings. The MP4 renders in that shape.
        </p>
      </figcaption>

      <ul className="bg-surface p-5 md:p-8 flex flex-col gap-6 md:grid md:grid-cols-[minmax(0,256fr)_minmax(0,81fr)_minmax(0,144fr)] md:items-start md:gap-8">
        {FORMATS.map((f) => (
          <li key={f.ratio} className="flex items-center gap-4 min-w-0 md:flex-col md:items-stretch md:gap-4">
            {/* Below md the frames sit in a fixed-width slot (the 16:9
                frame's width at h-20) so the labels line up. */}
            <div className="w-36 shrink-0 md:w-full">
              <Frame aspect={f.aspect} />
            </div>
            <div className="min-w-0">
              <span className="brutal-chip">{f.ratio}</span>
              <p className="font-display font-black text-base tracking-tight mt-1.5">{f.name}</p>
              {f.where && <p className="font-mono text-xs text-ink-muted">{f.where}</p>}
            </div>
          </li>
        ))}
      </ul>
    </figure>
  );
}
